import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  IconButton as MuiIconButton,
  TextField as MuiTextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { MemberService } from '../../services/member/memberService';
import { labelAboveSx } from '../../core/theme/formStyles';
import * as Sentry from '@sentry/react';
import { dialogActionsSx, dialogButtonSx } from '../../core/theme/buttonStyles';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-sm)',
    '& fieldset': {
      borderColor: 'var(--color-secondary)',
    },
  },
};

const validationSchema = Yup.object({
  currentPassword: Yup.string()
    .required('Current password is required')
    .min(6, 'Password must be at least 6 characters'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .required('Please confirm password')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
});

export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const memberService = new MemberService();

  const initialValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
        paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
      }}
      sx={{ padding: 0 }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t('members.changePassword')}
        <MuiIconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </MuiIconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              await memberService.changePassword(values.currentPassword, values.newPassword);
              notify(t('members.changePasswordDialog.passwordChangedSuccess'), { type: 'success' });
              resetForm();
              onClose();
            } catch (error: any) {
              if (error.code === 'auth/wrong-password') {
                notify(t('members.changePasswordDialog.wrongPassword'), { type: 'error' });
              } else if (error.code === 'auth/weak-password') {
                notify(t('members.changePasswordDialog.weakPassword'), { type: 'error' });
              } else if (error.code === 'auth/requires-recent-login') {
                notify(t('members.changePasswordDialog.requiresRecentLogin'), { type: 'error' });
              } else {
                notify(error.message || t('notifications.failedToChangePassword'), {
                  type: 'error',
                });
                Sentry.captureException(error);
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', ...labelAboveSx }}>
                <MuiTextField
                  name="currentPassword"
                  type="password"
                  label={t('members.changePasswordDialog.currentPassword') || 'Current Password'}
                  sx={inputSx}
                  size="small"
                  fullWidth
                  value={values.currentPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.currentPassword && Boolean(errors.currentPassword)}
                  helperText={(touched.currentPassword && errors.currentPassword) || ' '}
                />

                <MuiTextField
                  name="newPassword"
                  type="password"
                  label={t('members.changePasswordDialog.newPassword') || 'New Password'}
                  sx={inputSx}
                  size="small"
                  fullWidth
                  value={values.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.newPassword && Boolean(errors.newPassword)}
                  helperText={(touched.newPassword && errors.newPassword) || ' '}
                />

                <MuiTextField
                  name="confirmPassword"
                  type="password"
                  label={
                    t('members.changePasswordDialog.confirmNewPassword') || 'Confirm New Password'
                  }
                  sx={inputSx}
                  size="small"
                  fullWidth
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={(touched.confirmPassword && errors.confirmPassword) || ' '}
                />

                <Box sx={dialogActionsSx}>
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={onClose}
                    sx={dialogButtonSx}
                  >
                    {t('common.cancel')}
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={dialogButtonSx}
                  >
                    {t('common.saveChanges')}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
